import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Search, RefreshCw, X, Trash2, AlertTriangle, Download, GraduationCap } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

const STATUSES = [
  "new",
  "reviewed",
  "contacted",
  "academy_applicant",
  "future_chaplain_candidate",
  "hold",
  "not_selected",
  "closed",
];

const REASON_OPTIONS = [
  { value: "learn_about_workplace_chaplaincy", label: "Learn about workplace chaplaincy" },
  { value: "interested_in_becoming", label: "Interested in becoming a workplace chaplain" },
  { value: "experienced_chaplain_additional_training", label: "Experienced chaplain — additional training" },
  { value: "strengthen_corporate_skills", label: "Strengthen corporate/workplace skills" },
  { value: "future_ccchaps_opportunities", label: "Future CCCHAPS chaplain opportunities" },
  { value: "other", label: "Other" },
];
const reasonLabel = (v) => REASON_OPTIONS.find((o) => o.value === v)?.label || humanize(v);

const TRACK_OPTIONS = [
  { value: "workplace_development", label: "Workplace Chaplain Development" },
  { value: "continuing_education", label: "Continuing Education & Professional Development" },
  { value: "not_sure", label: "I'm not sure yet" },
];
const trackLabel = (v) => TRACK_OPTIONS.find((o) => o.value === v)?.label || null;

export default function AdminAcademyApplicants() {
  const { refreshOverview } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [trackFilter, setTrackFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (reasonFilter) params.primary_reason = reasonFilter;
      if (trackFilter) params.academy_track = trackFilter;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/admin/academy-applicants", { params });
      setItems(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, reasonFilter, trackFilter, q]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const r = await api.patch(`/admin/academy-applicants/${id}/status`, { status });
      setItems((p) => p.map((x) => (x.id === id ? r.data : x)));
      if (selected?.id === id) setSelected(r.data);
      toast.success("Status updated");
      refreshOverview?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const addNote = async () => {
    if (!noteDraft.trim() || !selected) return;
    try {
      const r = await api.patch(`/admin/academy-applicants/${selected.id}/notes`, { note: noteDraft.trim() });
      setSelected(r.data);
      setItems((p) => p.map((x) => (x.id === r.data.id ? r.data : x)));
      setNoteDraft("");
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const downloadResume = async (rec) => {
    try {
      const resp = await api.get(`/admin/academy-applicants/${rec.id}/resume`, { responseType: "blob" });
      const blob = new Blob([resp.data], {
        type: resp.headers["content-type"] || rec.resume_content_type || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = rec.resume_filename || `resume-${rec.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const doDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/academy-applicants/${selected.id}`);
      setItems((p) => p.filter((x) => x.id !== selected.id));
      setConfirmDelete(false);
      setSelected(null);
      toast.success("Application deleted");
      refreshOverview?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="admin-academy-applicants">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[#4A6FA5] mb-1">
            <GraduationCap className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
              CCCHAPS Academy
            </span>
          </div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Academy Applicants</h2>
          <p className="mt-1 text-sm text-slate-500">
            {items.length} record{items.length === 1 ? "" : "s"} · Separate from Careers applications
          </p>
        </div>
        <button
          onClick={load}
          data-testid="refresh-academy"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] self-start"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, role, city…"
            data-testid="search-academy"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="filter-academy-status"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanize(s)}
            </option>
          ))}
        </select>
        <select
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          data-testid="filter-academy-reason"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm max-w-[280px]"
        >
          <option value="">All reasons</option>
          {REASON_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <select
          value={trackFilter}
          onChange={(e) => setTrackFilter(e.target.value)}
          data-testid="filter-academy-track"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm max-w-[280px]"
        >
          <option value="">All tracks</option>
          {TRACK_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm" data-testid="empty-academy">
            No Academy applications yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                  <th className="text-left px-4 py-3 font-semibold">Applicant</th>
                  <th className="text-left px-4 py-3 font-semibold">Current role</th>
                  <th className="text-left px-4 py-3 font-semibold">Primary reason</th>
                  <th className="text-left px-4 py-3 font-semibold">Location</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50/60" data-testid={`academy-row-${a.id}`}>
                    <td className="px-4 py-3 text-slate-500 text-xs align-top">
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-slate-900 align-top">
                      <div className="font-medium">
                        {a.first_name} {a.last_name}
                      </div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                      {a.future_opportunities_optin && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold">
                          Future CCCHAPS opp opt-in
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700 align-top">{a.current_role}</td>
                    <td className="px-4 py-3 text-slate-700 align-top">
                      {reasonLabel(a.primary_reason)}
                      {a.academy_track && (
                        <div className="text-xs text-slate-500 mt-0.5" data-testid={`academy-track-cell-${a.id}`}>
                          Track: {trackLabel(a.academy_track)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700 align-top">
                      {a.city}, {a.state}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="inline-flex px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
                        {humanize(a.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <button
                        onClick={() => setSelected(a)}
                        data-testid={`view-academy-${a.id}`}
                        className="text-[#4A6FA5] hover:text-[#3B597B] text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="academy-drawer">
          <div className="flex-1 bg-slate-900/30" onClick={() => setSelected(null)} />
          <div className="w-full max-w-md bg-white border-l border-slate-200 overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">
                  Academy Applicant
                </p>
                <h3 className="font-heading text-xl text-slate-900">
                  {selected.first_name} {selected.last_name}
                </h3>
                <p className="mt-1 text-sm text-slate-600">{selected.current_role}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-500 hover:text-slate-900"
                data-testid="close-academy-drawer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Status</label>
              <select
                value={selected.status}
                onChange={(e) => updateStatus(selected.id, e.target.value)}
                data-testid="academy-drawer-status"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {humanize(s)}
                  </option>
                ))}
              </select>
            </div>

            {selected.resume_filename ? (
              <button
                onClick={() => downloadResume(selected)}
                data-testid="download-academy-resume"
                className="w-full mb-4 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
              >
                <Download className="w-4 h-4" /> Download résumé ({selected.resume_filename})
              </button>
            ) : (
              <div className="w-full mb-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500 text-center">
                No résumé attached
              </div>
            )}

            <dl className="space-y-3 text-sm">
              <Row label="Email" value={selected.email} />
              <Row label="Phone" value={selected.phone} />
              <Row label="Location" value={`${selected.city}, ${selected.state}`} />
              <Row label="Current role" value={selected.current_role} />
              <Row label="Primary reason" value={reasonLabel(selected.primary_reason)} />
              {selected.primary_reason === "other" && (
                <Row label="Explanation" value={selected.primary_reason_other} pre />
              )}
              <Row label="Academy track (applicant selection)" value={trackLabel(selected.academy_track)} />
              <Row
                label="Future CCCHAPS opportunities"
                value={selected.future_opportunities_optin ? "Yes — opted in" : "No"}
              />
              <Row label="Years of relevant experience" value={selected.years_experience} />
              <Row label="Languages" value={selected.languages} />
              <Row label="Chaplain / ministry / spiritual-care experience" value={selected.chaplain_experience} pre />
              <Row label="Education or chaplaincy training" value={selected.education_training} pre />
              <Row label="Workplace / corporate chaplaincy experience" value={selected.corporate_experience} pre />
              <Row label="Training interests" value={selected.training_interests} pre />
              <Row label="Availability" value={selected.availability} />
              <Row label="Additional information" value={selected.additional_information} pre />
              <Row label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
            </dl>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">
                Internal notes
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                {(selected.notes || []).length === 0 && (
                  <p className="text-xs text-slate-500">No notes yet.</p>
                )}
                {(selected.notes || []).map((n) => (
                  <div key={n.id} className="rounded-lg bg-[#F8FAFC] border border-slate-200 p-3 text-sm">
                    <p className="text-slate-800 whitespace-pre-wrap">{n.text}</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {n.author} · {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={3}
                data-testid="academy-note-input"
                placeholder="Add an internal note (not visible to applicant)…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
              />
              <button
                onClick={addNote}
                disabled={!noteDraft.trim()}
                data-testid="academy-note-save"
                className="mt-2 inline-flex items-center px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] disabled:opacity-50"
              >
                Save note
              </button>
            </div>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-red-700 font-semibold mb-2">Danger zone</p>
              <p className="text-xs text-slate-500 mb-2">
                Use only for spam, test, duplicate, or clearly invalid submissions. Any attached résumé will also be removed.
              </p>
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-academy-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" /> Delete Academy application
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4"
          data-testid="academy-delete-modal"
          onClick={() => !deleting && setConfirmDelete(false)}
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-600 shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </span>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  Delete this Academy application permanently?
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  This will also mark any attached résumé file for permanent removal. Use only for spam/test/invalid records.
                </p>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm mb-6">
              <div>
                <span className="text-slate-500">Applicant: </span>
                <span className="text-slate-900 font-medium">
                  {selected.first_name} {selected.last_name}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Email: </span>
                <span className="text-slate-900">{selected.email}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={deleting}
                data-testid="confirm-delete-academy"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {deleting ? (
                  "Deleting…"
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" /> Delete permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, pre }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</dt>
      <dd className={`text-slate-800 mt-0.5 ${pre ? "whitespace-pre-wrap" : ""}`}>{value}</dd>
    </div>
  );
}
